import { S, announce } from './state';
import * as A from './actions';
import * as Router from './router';
import * as Session from './session';
import * as Presence from './presence';
import * as MapSync from './mapSync';
import * as Undo from './undo';
import * as Writes from './mapWrites';
import { apiRequest, configureUnauthorised, configureMaintenance } from '../api/client';
import { clearUserPermissions, clearViewRecord, readDurablePrefs } from '../store/persistence';
import { getStoreState } from '../store';
import { BOOT_ORDER, type BootPhase } from '../store/slices/boot';
import { applyPrefs, applySidebar, initChrome } from './chrome';
import { requestTopbarFit } from '../features/topbar/topbarFit';
import type { Language } from './state';

/**
 * BOOT — the one sequence in the app whose ORDER is a correctness property.
 *
 * `CLAUDE.md` states three rules about per-user view state, and until the flip they were
 * guaranteed by the order of statements inside a function and by nothing else. They are the
 * same three rules, and they are enforced here plus in `store/slices/boot.ts`, which derives
 * the write guard from the phase rather than from a comment:
 *
 *  1. `restoreViewState()` runs AFTER `loadMapFromServer()`, which re-collapses every node and
 *     would silently undo the restore.
 *  2. …and AFTER `applyPermissions()`, which populates the hidden sets the restore validates
 *     the stored ids against.
 *  3. `viewStateReady` gates every view-state WRITE until that read has happened, or the
 *     default-state setup that runs before it — `applyPermissions()` calling
 *     `setAiDocMode(false)`, `setEditing()` doing the same — persists an empty view over the
 *     one about to be loaded.
 *
 * A fourth rule comes from `DEPLOYMENTS.md` and is enforced by `boot()`'s own order:
 * capabilities are read BEFORE any chrome is drawn, or the AI buttons flash in and then out.
 *
 * ── THE PHASE IS ANNOUNCED, NOT INFERRED ─────────────────────────────────────────────────
 * `enterPhase()` walks the store's boot machine one step at a time, and the machine refuses an
 * out-of-order move. That is what turns the sequence above from "the order these lines happen
 * to be in" into something a test and a runtime check can both see.
 */

function enterPhase(p: BootPhase): void {
  S.bootPhase = p;
  const st = getStoreState();
  if (p === 'booting') {
    st.resetBoot();
  } else {
    // One step at a time even when catching up, so `enterBootPhase`'s ordering guard stays the
    // only way the phase ever moves.
    const want = BOOT_ORDER.indexOf(p);
    let guard = BOOT_ORDER.length;
    while (BOOT_ORDER.indexOf(getStoreState().bootPhase) < want && guard-- > 0) {
      const next = BOOT_ORDER[BOOT_ORDER.indexOf(getStoreState().bootPhase) + 1];
      if (!getStoreState().enterBootPhase(next)) break;
    }
  }
  announce();
}

/* ------------------------------------------------------------------------- the session */

export async function establishSession(): Promise<boolean> {
  const info = await Session.establish();
  S.currentUser = info.currentUser;
  S.perm = info.perm;
  return !!S.currentUser;
}

export async function attemptLogin(username: string, password: string): Promise<boolean> {
  if (!(await Session.login(username, password))) return false;
  return establishSession();
}

/**
 * Everything that has to stop being true once there is no session.
 *
 * Extracted from `logout()` so a LOST session (V0.45) reaches exactly the same state as a
 * deliberate sign-out. Two things stay OUTSIDE it, because they are the differences between
 * the two: `Session.end()` (nobody asked the server to end anything — it already has) and
 * `clearViewRecord()` (nobody chose to leave, so the day's open branches wait for them).
 */
function resetSignedOutState(): void {
  S.currentUser = null;
  S.perm = null;
  // Per-user state must not survive into the login screen or the next user's session.
  S.myItems = { favorites: [], recents: [], loaded: false };
  S.myAssignments = { nodes: [], docs: [], loaded: false };
  S.subscriptions = new Set();
  S.subscriptionsLoaded = false;
  S.feedUnread = 0;
  S.docWorkflow = {};
  S.docWorkflowLoaded = false;
  S.userDirectory = [];
  S.userDirectoryLoaded = false;
  S.heatmapOn = false;
  S.mapHealth = { byNode: {}, loaded: false };
  A.refreshHiddenSets();
  document.getElementById('permmodal')?.classList.remove('show');
  A.showLogin();
}

export async function logout(): Promise<void> {
  Presence.disconnectPresence();
  const keycloakLogout = await Session.end();
  // Sign-out hygiene (RGPD): drop THIS user's day-scoped view record from the shared browser.
  // Before `S.currentUser` is cleared, because the id is what the record is keyed on. Only
  // here — session expiry never calls logout(), so an expired session deliberately keeps the
  // record for the user's return.
  if (S.currentUser) clearViewRecord(S.currentUser.id);
  resetSignedOutState();
  // A Keycloak session ends at Keycloak too (V0.54.3): a full-page navigation, not a fetch —
  // `connect-src 'self'` is untouched — which lands back on the app, signed out, on the login
  // card. Last, so everything above has already happened if the navigation is slow.
  if (keycloakLogout) window.location.assign(keycloakLogout);
}

/**
 * Is THIS 401 the end of a session?
 *
 * Pure, and its own function so the two exclusions are testable rather than buried in a
 * handler. Both of them are cases where a 401 is the correct, expected answer:
 *
 *  - **Boot's own `/api/auth/me`.** "Who am I" answers 401 with "nobody", which is how the
 *    login card comes up in the first place. Announcing a lost session there would greet a
 *    first-time visitor with "your session ended".
 *  - **Any `/api/auth/*` route.** A wrong password is a 401 about the credentials, not about
 *    a session — the login form reports it itself, and it must not be overwritten with a
 *    notice saying something else happened.
 *
 * It also dedupes: once the session is gone `signedIn` is false, so a burst of in-flight
 * requests all coming back 401 announces once.
 */
export function shouldEndSession(url: string, at: { booted: boolean; signedIn: boolean }): boolean {
  if (!at.booted || !at.signedIn) return false;
  // Match the PATH, so a query string cannot smuggle the segment past this. The dummy base
  // resolves a relative url and is ignored by an absolute one — and keeps this window-free,
  // which is what lets it be a plain unit test.
  try {
    return !new URL(url, 'http://base.invalid').pathname.startsWith('/api/auth/');
  } catch {
    return !url.startsWith('/api/auth/');
  }
}

/**
 * The session is gone — say so, and stop pretending otherwise.
 *
 * Reached from ONE place: `configureUnauthorised` in api/client.ts, on any 401 from any
 * route. Before this, a lost session was whatever each caller does with an error, which in
 * practice meant an empty panel and a console line: the walkthrough that found this watched
 * three screens come up blank with nothing on them explaining why.
 *
 * UNSENT EDITS ARE DROPPED AND NAMED, not queued. After signing in again the map is reloaded
 * from the server, so a kept dirty id would flush the server's own value straight back — and
 * label it as this user's edit. Saying "some edits could not be saved" is honest; silently
 * replaying stale values is not.
 */
export function onSessionLost(url: string): void {
  if (!shouldEndSession(url, { booted: S.booted, signedIn: !!S.currentUser })) return;

  Presence.disconnectPresence();
  // Both answer "was anything owed", and both are called for the answer as much as the act.
  const owedInFlight = A.cancelServerSync();
  const owedMarks = Writes.discardOwed();
  // The suite-wide contract: other CONIGON apps read this key, and it must not go on
  // advertising permissions for a session that has ended.
  clearUserPermissions();
  resetSignedOutState();

  S.routeNotice = 'session-ended';
  S.noticeDetail =
    owedInFlight || owedMarks ? 'Edits made in the last moments could not be saved.' : null;
  announce();
}

export async function loadCapabilities(): Promise<void> {
  S.caps = await Session.capabilities();
}

/* ------------------------------------------------------------------------ the language */

/**
 * The locale list, from `GET /api/languages`.
 *
 * Open-ended rather than a fixed enum, and hidden entirely when there is only one language so
 * it does not clutter the toolbar for maps that are not translated.
 */
export async function initLanguageSwitcher(): Promise<void> {
  try {
    S.languages = await apiRequest<Language[]>('/api/languages');
  } catch {
    return;
  }
  // Every surface that carries a picker renders its own, so all this owes them is the list
  // and a nudge to re-render with it. There used to be a `mountLangPickers()` here that also
  // walked `.perm-head, .docbar` and injected a picker into any header that had none; deep
  // links made that walk start finding headers mid-render — see chrome.ts's history — and it
  // duplicated the very pickers it was meant to stand in for.
  announce();
}

export async function setLocale(code: string): Promise<void> {
  if (!code || code === S.currentLocale) return;
  S.currentLocale = code; // every picker is controlled from the snapshot (V0.53.5): they all move
  try {
    await A.loadMapFromServer();
    A.autoLayout('view'); // re-deriving MY layout after a language switch is not an edit
    A.render();
    A.renderTags();
    A.rebuildPresence();
  } catch {
    alert('Could not load the map in that language.');
    return;
  }
  // Workflow state is per page AND LOCALE, so the map loaded for the previous language says
  // nothing about this one's translations. Not awaited, like its call in `enterApp()`.
  void A.loadDocWorkflows();
  refreshOpenViews();
  // A locale switch is a NAVIGATION now (plan 1.6): the URL's locale segment is rewritten,
  // pushed so Back returns to the previous language. Suppressed by the router's latch when
  // this call itself came from a popstate — which is exactly Back doing that return.
  Router.syncUrl('nav');
}

/**
 * Whatever is on screen was rendered in the previous language.
 *
 * Re-open it rather than leaving a half-translated view behind.
 */
function refreshOpenViews(): void {
  A.renderDocLogs(); // history is per-language
  if (S.docOpen && S.docCurrentId) {
    // ONE opener (V0.53.5): the mode is the app's and the editor follows it; the sequence bump
    // inside re-runs the editor's fetch and its sitting for the new language.
    A.openDoc(
      A.docUrlFor(S.docCurrentId),
      S.docCurrentTitle || 'Documentation',
      S.docCurrentId,
      S.docNavNodeId,
    );
  }
}

/* -------------------------------------------------------------------------- enter the app */

/** Enter InfoStation after a successful sign-in, or a restored session. */
export async function enterApp(): Promise<void> {
  A.hideLogin();
  // "Your session ended" has been answered by getting back in. Cleared here rather than by
  // the login form, so it goes away on every route into the app — including the OIDC round
  // trip, which never touches that form.
  if (S.routeNotice === 'session-ended') {
    S.routeNotice = null;
    S.noticeDetail = null;
  }
  // Suppress view-state writes until this user's record has been READ. A second person signing
  // in on this browser boots again from scratch, which is why this is not a one-shot.
  S.viewStateReady = false;
  enterPhase('booting');

  // The arrival URL, parsed and parked (plan 1.6). PURE except for one thing: the URL's
  // locale segment feeds the `loadMapFromServer()` below, whose response carries the
  // server-RESOLVED locale — which is how an unknown segment lands on the default and gets
  // its URL corrected when the route is applied.
  Router.stashRoute();

  try {
    await A.loadMapFromServer();
  } catch (e) {
    console.error('Failed to load map from server:', e);
    alert('Could not load the map from the server. Please refresh to try again.');
  }
  enterPhase('mapLoaded');

  void initLanguageSwitcher();
  A.applyPermissions();
  A.setEditing(S.editing && A.canEnterEdit()); // the saved edit state, if the role still allows it
  enterPhase('permissionsApplied');

  // "My items" (plan 2.4) — per-user server state, loaded once the session is established.
  // NOT awaited: the pane is additive chrome, and a failure means an empty pane rather than
  // a blocked boot. After applyPermissions(), so the render-time visibility filtering the
  // pane does has the hidden sets to filter against by the time the rows appear.
  void A.loadMyItems();
  // The bell (plan Phase 3) — same contract: subscriptions for the watch toggles, and the
  // unread count for the badge. Sign-in IS the bell's "refresh on mount": the region mounts
  // before boot, so there is nothing useful for it to fetch any earlier than this.
  void A.loadSubscriptions();
  void A.refreshFeedBadge();
  // "My assignments" (V0.38) — the same contract as loadMyItems beside it: bare refs, never
  // awaited, a failure is an empty section.
  void A.loadMyAssignments();
  // "My to-dos" (V0.50) — same contract once more, with one difference of its own: it
  // self-gates on VIEW_TODOS and skips the request entirely without it, so a reader who has
  // no to-do feature does not spend a guaranteed 403 on every sign-in.
  void A.loadMyTodos();
  // The project marks' progress figures (V0.50.3) — the same contract and the same gate: the
  // heatmap underline on a project node has nothing to show a reader who may not see to-dos.
  void A.loadProjectProgress();
  // The documentation workflow + the directories — the same contract again, with one extra
  // gate of their own: both endpoints are participant-gated, so each returns early for a
  // reader rather than spending a guaranteed 403 on every sign-in. After `applyPermissions()`,
  // which is what decides that. `loadDocWorkflows()` is PER LOCALE, so `setLocale()` calls it
  // a second time.
  void A.loadDocWorkflows();
  void A.loadUserDirectory();

  // Stage 1b (routing) does its work HERE, between the two announcements: the target of a
  // shared link is resolved AFTER permissions, so a hidden node reports "not permitted" rather
  // than leaking its existence through "not found", and BEFORE the view restore, so a link's
  // own filters win over the stored ones — the resolve records which filters the URL names
  // explicitly, and restoreViewState() skips exactly those.
  Router.resolvePendingRoute();
  enterPhase('urlParsed');

  // Rule 1 and rule 2: after the map load and after permissions. See the note at the top.
  const rv = A.restoreViewState();
  S.viewStateReady = true;
  enterPhase('viewRestored');

  A.render();
  A.renderTags();
  // Boot lays the tree out for THIS session. It writes nothing: every client does this on
  // arrival, and a map that PUT its own opening layout is how a fresh sign-in used to issue a
  // full structural write ~840 ms after the login card closed.
  A.autoLayout('view');
  A.render();
  // applySearch() re-lays the tree, so it runs here rather than inside the restore.
  if (rv.restored && S.searchText) A.applySearch();
  // The parked route lands only now, when the regions it opens and the layout it centres
  // against both exist. It also marks the router ready to WRITE URLs, and replaces the
  // arrival URL with its canonical form (server-resolved locale included).
  const nav = Router.applyPendingRoute();
  // A fresh work day, or a first visit, starts centred. The camera is restored explicitly, so a
  // stale record must actively re-centre rather than keep wherever it was left. A node target
  // centres on ITSELF instead — never fight that camera with a fit.
  if (!rv.camera && !nav.centered) setTimeout(() => A.fit(), 40);
  requestTopbarFit();
  enterPhase('ready');
}

/* ----------------------------------------------------------------------------- boot */

/**
 * Hand each subsystem the part of the app it cannot know for itself.
 *
 * Three hand-offs of the same shape: the module owns the mechanism, this side owns the answer.
 */
function wireSubsystems(): void {
  // The structural delta reads the map at FLUSH time; a 409 means somebody else saved
  // first, which is not a fault to report but a reason to take their version.
  Writes.configureStructuralSync({
    snapshot: A.structuralSnapshot,
    version: () => S.dataVersion,
    onVersion: (v) => {
      S.dataVersion = v;
    },
    // A refused write is a BANNER AND A REFETCH now, not an alert and a reload (V0.34). The
    // reload was the reported bug's second half: it discarded the camera, every open branch,
    // the open document and any unsaved popup draft — for a conflict the user usually had not
    // caused, since view-only actions used to write. `mapSync` parks the rejected change,
    // refetches in place, and says what happened.
    onConflict: MapSync.noteWriteRejected,
    // A delta refused for MISSING BRICKS (V0.37) is the same shape with a name attached: the
    // banner says which permission it was, the refused change is parked, the map is refetched.
    onForbidden: MapSync.noteWriteForbidden,
  });
  Undo.setUndoListener(() => announce());
  Presence.configurePresence({
    loc: A.myPresenceLoc,
    onRoster: A.rebuildPresence,
    // Re-asked before every reconnect attempt, so signing out or losing the role really ends
    // it rather than being undone by a timer that was already in flight.
    allowed: () => !!S.currentUser && A.canPresence(),
    // The live half: the socket carries the mechanism, this side owns the answer.
    onMapChanged: MapSync.noteRemoteVersion,
    // A reconnect says the server went away and came back — a restart, or a restore closing
    // every socket on purpose (V0.48). Asking for the version then is what takes a tab that
    // sat through a restore off the old map without the reader touching anything, and
    // `noteServerBack` inside it is what clears the banner.
    onOpen: () => void MapSync.checkMapVersion(),
  });
  // An admin editing this user's role changes what the SERVER will send them, and no
  // dataVersion moves to announce it (V0.43). `actions.ts` cannot reach `mapSync` itself —
  // the import runs one way only — so the two are introduced here, like everything else.
  A.configurePermissionSync(MapSync.notePermissionsChanged);
  // A 401 from any route means the session is gone (V0.45). One sink, wired once, so no
  // caller has to remember to check for it — and none of them did.
  configureUnauthorised(onSessionLost);
  // And a `maintenance` 503 from any route means an administrator is restoring a backup
  // (V0.48) — the same shape of fact, wired the same way and for the same reason.
  configureMaintenance(MapSync.noteMaintenance);
}

/**
 * The fallback half of "the map changed" — and the dropped-socket safety net.
 *
 * Applies to EVERYONE, not only users without `SHOW_PRESENCE`: a socket that quietly died
 * leaves a presence user exactly as stale as one who never had a socket, and neither of them
 * can tell. `focus` and a `visibilitychange` back to visible are the two moments a stale view
 * starts mattering again; there is deliberately NO interval poll (this codebase's anti-polling
 * stance is explicit in `state.ts` and `FeedBell.tsx`), and a tab nobody is looking at has
 * nothing worth refreshing.
 *
 * WIRED ONCE, with `initRouter()`'s latch idiom rather than inside a region: React StrictMode
 * mounts every region's effects twice in development, which would register two listeners and
 * ask the server twice per focus. `checkMapVersion()` swallows its own failures — this runs on
 * every focus, and `e2e/13-offline.spec.ts` watches the console as closely as the wire.
 */
let versionListenersWired = false;

function wireVersionFallback(): void {
  if (versionListenersWired) return;
  versionListenersWired = true;
  window.addEventListener('focus', () => void MapSync.checkMapVersion());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void MapSync.checkMapVersion();
  });
}

/**
 * Start the app.
 *
 * The sign-in check comes first: the map is only fetched once there is a session, and no map
 * content is cached in the browser at all.
 */
export async function boot(): Promise<void> {
  wireSubsystems();
  initChrome();
  Router.initRouter(); // the popstate listener, wired once (the initChrome idiom)
  wireVersionFallback(); // focus + visibilitychange -> GET /api/map/version, wired once

  applyPrefs(readDurablePrefs());
  applySidebar(); // after the prefs: it uses the restored width and open state
  A.syncScopeUI(); // after the prefs: it corrects the restored scope for this user
  document.body.classList.toggle('light', S.isLight);

  // BEFORE anything is drawn. `applyPermissions()` decides which chrome exists, and the
  // capability half of that decision has to be known by then or the AI buttons flash in and
  // then out (DEPLOYMENTS.md).
  await loadCapabilities();

  if (await establishSession()) await enterApp();
  else A.showLogin();

  // Last: `booted` is what tells the login overlay the session question has actually been
  // asked, as opposed to not answered yet.
  S.booted = true;
  announce();
}
